import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { addDays, startOfDay } from "date-fns";
import { useTenant } from "@/hooks/useTenant";
import { useTrailerTypes } from "@/hooks/useTrailerTypes";
import {
  ordersToCsv,
  downloadCsv,
  buildExportFilename,
  type ExportableOrder,
} from "@/lib/orders-csv";
import type { OrderStatus, Location } from "@/types/tms";
import type { DateRange } from "@/hooks/useOrdersQuery";

const PAGE_SIZE = 1000;

export interface UseOrdersExportParams {
  searchQuery: string;
  statusFilter: string | string[];
  dateFilter: DateRange;
  tenderFilter?: string;
  bolFilter?: string;
  tenderIdFilter?: string;
  trailerTypeFilter?: string | string[];
  trailerLoadStatusFilter?: string | string[];
  apiCreatedFilter?: string;
  tripTypeFilter?: string | string[];
  orderTypeFilter?: string;
  shipFromLocationFilter?: string | string[];
  shipToLocationFilter?: string | string[];
  customerFilter?: string | string[];
  locations?: Location[];
}

export function useOrdersExport(params: UseOrdersExportParams) {
  const { tenantId } = useTenant();
  const { data: trailerTypes } = useTrailerTypes();
  const [isExporting, setIsExporting] = useState(false);

  const exportToCsv = useCallback(async () => {
    if (!tenantId) return;
    setIsExporting(true);
    try {
      const normalize = (f: string | string[] | undefined) => {
        const raw = Array.isArray(f) ? f : f ? [f] : [];
        return raw.filter((v) => v && v !== "all");
      };
      const selectedStatuses = normalize(params.statusFilter);
      const selectedShipFromIds = normalize(params.shipFromLocationFilter);
      const selectedShipToIds = normalize(params.shipToLocationFilter);
      const selectedTripTypes = normalize(params.tripTypeFilter);
      const selectedCustomerIds = normalize(params.customerFilter);
      const selectedTrailerTypeIds = normalize(params.trailerTypeFilter);
      const selectedTrailerLoadStatuses = normalize(params.trailerLoadStatusFilter);

      // Pre-resolve search-driven ID expansions (same as useOrdersQuery).
      let searchIdParts: string[] = [];
      const searchText = params.searchQuery.trim();
      if (searchText) {
        const [customerRes, driverRes, tractorRes] = await Promise.all([
          supabase.from("customers").select("id").ilike("organization_name", `%${searchText}%`),
          supabase.from("drivers").select("id").ilike("name", `%${searchText}%`),
          supabase.from("tractors").select("id").ilike("name", `%${searchText}%`),
        ]);
        searchIdParts = [
          `unique_id.ilike.%${searchText}%`,
          `ship_from_city.ilike.%${searchText}%`,
          `ship_to_city.ilike.%${searchText}%`,
          `trailer_id.ilike.%${searchText}%`,
          `tender_id.ilike.%${searchText}%`,
        ];
        const cIds = customerRes.data?.map((c) => c.id) || [];
        const dIds = driverRes.data?.map((d) => d.id) || [];
        const tIds = tractorRes.data?.map((t) => t.id) || [];
        if (cIds.length) searchIdParts.push(`billing_party_id.in.(${cIds.join(",")})`);
        if (dIds.length) searchIdParts.push(`assigned_operator_id.in.(${dIds.join(",")})`);
        if (tIds.length) searchIdParts.push(`assigned_tractor_id.in.(${tIds.join(",")})`);
      }

      // Document presence/absence filters are expressed as join embeds (same as
      // useOrdersQuery) so we never pull the whole order_bol_images /
      // order_tender_images table into the browser just to build an id list —
      // that produced ~100KB request URLs and a hard "Failed to export".
      const bolFilter = params.bolFilter && params.bolFilter !== "all" ? params.bolFilter : null;
      const tenderFilter =
        params.tenderFilter && params.tenderFilter !== "all" ? params.tenderFilter : null;

      // Trailer Load Status filter — resolve to matching trailer_id text values.
      let trailerLoadStatusTrailerIds: string[] | null = null;
      if (selectedTrailerLoadStatuses.length > 0) {
        const { data: trailerRows } = await supabase
          .from("trailers")
          .select("trailer_id, load_status")
          .eq("tenant_id", tenantId)
          .in("load_status", selectedTrailerLoadStatuses as any);
        trailerLoadStatusTrailerIds = [
          ...new Set((trailerRows ?? []).map((t) => t.trailer_id).filter(Boolean) as string[]),
        ];
      }

      // Single/multi leg filter via legs table.
      let multiLegParentIds: string[] | null = null;
      if (params.orderTypeFilter === "single_leg" || params.orderTypeFilter === "multi_leg") {
        const { data: legRows } = await supabase
          .from("legs")
          .select("order_id")
          .eq("tenant_id", tenantId);
        const counts = new Map<string, number>();
        for (const r of legRows ?? []) {
          if (!r.order_id) continue;
          counts.set(r.order_id, (counts.get(r.order_id) ?? 0) + 1);
        }
        multiLegParentIds = [...counts.entries()]
          .filter(([, c]) => c > 1)
          .map(([id]) => id);
      }

      const esc = (v: string) => `"${(v ?? "").replace(/"/g, '\\"')}"`;

      const applyFilters = (q: ReturnType<typeof buildBase>) => {
        let query = q;
        if (selectedStatuses.length > 0) {
          query = query.in("status", selectedStatuses as OrderStatus[]);
        }
        if (selectedCustomerIds.length > 0) {
          query = query.in("billing_party_id", selectedCustomerIds);
        }
        if (searchIdParts.length > 0) {
          query = query.or(searchIdParts.join(","));
        }
        if (params.dateFilter) {
          const rangeStart = startOfDay(params.dateFilter.from).toISOString();
          const rangeEnd = startOfDay(addDays(params.dateFilter.to, 1)).toISOString();
          query = query.gte("ready_time", rangeStart).lt("ready_time", rangeEnd);
        }
        if (params.tenderIdFilter === "has") query = query.not("tender_id", "is", null);
        else if (params.tenderIdFilter === "missing") query = query.is("tender_id", null);

        // Anti-join: filter on the embedded RESOURCE (not one of its columns).
        if (tenderFilter === "missing") {
          query = query.is("order_tender_images", null);
        }

        if (selectedTrailerTypeIds.length > 0) {
          query = query.in("trailer_type_id", selectedTrailerTypeIds);
        }

        if (trailerLoadStatusTrailerIds !== null) {
          if (trailerLoadStatusTrailerIds.length === 0) {
            query = query.eq("id", "00000000-0000-0000-0000-000000000000");
          } else {
            query = query.in("trailer_id", trailerLoadStatusTrailerIds);
          }
        }

        if (params.apiCreatedFilter === "api") {
          query = query.eq("is_api_created", true);
        } else if (params.apiCreatedFilter === "manual") {
          query = query.eq("is_api_created", false);
        }

        if (bolFilter === "missing") {
          query = query.is("order_bol_images", null);
        }

        if (selectedTripTypes.length > 0) {
          query = query.in("trip_type", selectedTripTypes as never);
        }

        if (multiLegParentIds !== null) {
          if (params.orderTypeFilter === "multi_leg") {
            if (multiLegParentIds.length === 0) {
              query = query.eq("id", "00000000-0000-0000-0000-000000000000");
            } else {
              query = query.in("id", multiLegParentIds);
            }
          } else if (multiLegParentIds.length > 0) {
            query = query.not("id", "in", `(${multiLegParentIds.join(",")})`);
          }
        }

        if (selectedShipFromIds.length > 0) {
          const selectedLocs = (params.locations ?? []).filter((l) => selectedShipFromIds.includes(l.id));
          if (selectedLocs.length === 0) {
            query = query.eq("id", "00000000-0000-0000-0000-000000000000");
          } else {
            const orParts = selectedLocs.map((loc) => {
              const addr = esc(loc.street_address ?? "");
              const city = esc(loc.city ?? "");
              return `and(ship_from_street_address.eq.${addr},ship_from_city.eq.${city})`;
            });
            query = query.or(orParts.join(","));
          }
        }
        if (selectedShipToIds.length > 0) {
          const selectedLocs = (params.locations ?? []).filter((l) => selectedShipToIds.includes(l.id));
          if (selectedLocs.length === 0) {
            query = query.eq("id", "00000000-0000-0000-0000-000000000000");
          } else {
            const orParts = selectedLocs.map((loc) => {
              const addr = esc(loc.street_address ?? "");
              const city = esc(loc.city ?? "");
              return `and(ship_to_street_address.eq.${addr},ship_to_city.eq.${city})`;
            });
            query = query.or(orParts.join(","));
          }
        }

        return query;
      };

      // Keep the select string opaque to the type-level parser (perf).
      const sel = (s: string): string => s;
      const embeds = [
        bolFilter === "has"
          ? "order_bol_images!inner(order_id)"
          : bolFilter === "missing"
            ? "order_bol_images!left(order_id)"
            : null,
        tenderFilter === "has"
          ? "order_tender_images!inner(order_id)"
          : tenderFilter === "missing"
            ? "order_tender_images!left(order_id)"
            : null,
      ].filter(Boolean) as string[];

      const selectClause = [
        "*",
        "billing_party:customers(*)",
        "assigned_tractor:tractors(*)",
        "assigned_operator:drivers!assigned_operator_id(*)",
        "assigned_operator_2:drivers!assigned_operator_2_id(*)",
        ...embeds,
      ].join(",");

      const buildBase = () =>
        supabase
          .from("orders")
          .select(sel(selectClause))
          .eq("tenant_id", tenantId)
          .eq("is_work_order", false)
          .is("parent_order_id", null);

      const collectedRaw: ExportableOrder[] = [];
      let offset = 0;
      // Hard safety cap to avoid runaway loops.
      const HARD_CAP = 50_000;
      while (collectedRaw.length < HARD_CAP) {
        let q = applyFilters(buildBase());
        q = q.order("ready_time", { ascending: true }).range(offset, offset + PAGE_SIZE - 1);
        const { data, error } = await q;
        if (error) throw error;
        const batch = (data ?? []) as unknown as ExportableOrder[];
        collectedRaw.push(...batch);
        if (batch.length < PAGE_SIZE) break;
        offset += PAGE_SIZE;
      }

      // An inner-join embed can repeat a parent order once per related document row.
      const seenIds = new Set<string>();
      const collected: ExportableOrder[] = [];
      for (const o of collectedRaw) {
        const id = (o as { id?: string }).id;
        if (id) {
          if (seenIds.has(id)) continue;
          seenIds.add(id);
        }
        collected.push(o);
      }

      // Enrich with trailer load_status keyed by trailer_id text.
      const trailerIds = [
        ...new Set(
          collected.map((o) => o.trailer_id).filter((v): v is string => !!v),
        ),
      ];
      const trailerLoadStatusByTrailerId = new Map<string, string | null>();
      if (trailerIds.length > 0) {
        // Chunk to keep URL length safe.
        const CHUNK = 200;
        for (let i = 0; i < trailerIds.length; i += CHUNK) {
          const slice = trailerIds.slice(i, i + CHUNK);
          const { data: rows } = await supabase
            .from("trailers")
            .select("trailer_id, load_status")
            .eq("tenant_id", tenantId)
            .in("trailer_id", slice);
          for (const r of rows ?? []) {
            if (r.trailer_id) trailerLoadStatusByTrailerId.set(r.trailer_id, r.load_status ?? null);
          }
        }
      }

      // Delivered orders export the historical (at-delivery) load status.
      const loadStatusAtDelivery = new Map<string, { load_status: string | null }>();
      const deliveredIds = collected
        .filter((o) => o.status === "delivered")
        .map((o) => (o as { id?: string }).id)
        .filter((v): v is string => !!v);
      if (deliveredIds.length > 0) {
        const CHUNK_IDS = 500;
        for (let i = 0; i < deliveredIds.length; i += CHUNK_IDS) {
          const { data: rows } = await supabase.rpc("order_load_status_at_delivery", {
            p_order_ids: deliveredIds.slice(i, i + CHUNK_IDS),
          });
          for (const r of (rows ?? []) as Array<{ order_id: string; load_status: string | null }>) {
            if (r.order_id) loadStatusAtDelivery.set(r.order_id, { load_status: r.load_status });
          }
        }
      }

      const csv = ordersToCsv(collected, {
        locations: params.locations,
        trailerTypes: trailerTypes ?? undefined,
        trailerLoadStatusByTrailerId,
        loadStatusAtDelivery,
      });
      downloadCsv(csv, buildExportFilename());
      return { count: collected.length };
    } finally {
      setIsExporting(false);
    }
  }, [tenantId, trailerTypes, params]);

  return { exportToCsv, isExporting };
}